import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError, TagDescription } from '@reduxjs/toolkit/query';

/**
 * RTK Query passes `(result, error, arg, meta)` into tag callbacks. For **mutations**
 * (`invalidatesTags`) and **queries** (`providesTags`), it still invokes them when the
 * thunk **fails with `rejectWithValue`** (typical HTTP errors from `fetchBaseQuery`).
 *
 * Without checking `error`, failed requests can still register tags (e.g. `LIST`),
 * which skews cache invalidation / refetch behavior.
 *
 * Helpers below return tags **only when `error` is absent** so failures stay untagged.
 *
 * Use `TagDescription` so handlers stay assignable to RTK’s `ResultDescription`.
 *
 * @see `@reduxjs/toolkit/query` invalidation & `writeProvidedTagsForQueries` matchers.
 */
export type RtkqMutationError = FetchBaseQueryError | SerializedError | undefined;

/** Alias: same union as mutations — queries use it for `providesTags` error arg. */
export type RtkqQueryError = RtkqMutationError;

/** Drop query data as soon as the last subscriber unmounts — no RTK persistence. */
export const NO_RTK_CACHE = { keepUnusedDataFor: 0 } as const;

export function invalidateTagsOnSuccessConst<const Tags extends readonly TagDescription<string>[]>(
  tags: Tags,
): (
  _result: unknown,
  error: RtkqMutationError,
  _arg?: unknown,
  _meta?: unknown,
) => Tags | readonly [] {
  return (_result, error) => {
    if (error != null) return [];
    return tags;
  };
}

/** Same behavior as `invalidateTagsOnSuccessConst` — use on queries for readability. */
export const providesTagsOnSuccessConst = invalidateTagsOnSuccessConst;

export function invalidateTagsOnSuccess<
  Result,
  Arg,
  const Tags extends readonly TagDescription<string>[],
>(getTags: (result: Result | undefined, arg: Arg) => Tags) {
  return (
    result: Result | undefined,
    error: RtkqMutationError,
    arg: Arg,
    _meta?: unknown,
  ): Tags | readonly [] => {
    if (error != null) return [];
    return getTags(result, arg);
  };
}

/** Query `providesTags`: delegate only after success — matches RTK `(result, error, arg, meta)`. */
export function providesTagsOnSuccess<
  Result,
  Arg,
  const Tags extends readonly TagDescription<string>[],
>(getTags: (result: Result | undefined, arg: Arg) => Tags) {
  return invalidateTagsOnSuccess(getTags);
}

/**
 * Paginated list pattern: tag each `{ id }` plus `{ id: 'LIST' }`. Empty `data` still gets `LIST`.
 * Pass a **string literal** `tagType` (e.g. `'Client'`) so tags match your API’s `tagTypes`.
 */
export function providesPaginatedListTags<const TagType extends string>(tagType: TagType) {
  return (
    result: { data: readonly { id: string }[] } | undefined,
    error: RtkqMutationError,
    ..._rest: unknown[]
  ) => {
    if (error != null) return [];
    const rows = result?.data ?? [];
    const itemTags = rows.map((item) => ({ type: tagType, id: item.id }) as const);
    return [...itemTags, { type: tagType, id: 'LIST' } as const];
  };
}
